import { useMemo, useState } from "react";
import { Button, ModalLayout } from "@wac-ui-dashboard/wac_component_library";
import { toast } from "react-toastify";
import { useThumbnailList, MEDIA_TYPE } from "../useMenuThumbnails";
import { getRatioLabel, matchesRatio } from "../ratio";
import Style from "./thumbnailPicker.module.scss";

// Pick an already uploaded menu thumbnail for an image field in the Podcast/Video
// form instead of uploading a new file. Only images with the field's ratio can be
// selected.

// the form expects a File (like the cropper gives), so download the stored image
const toFile = async (url, fileName) => {
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) throw new Error("Could not read the image");
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type || "image/png" });
};

const ThumbnailPickerModal = ({ show, handleClose, mediaType, ratio, label, onSelect }) => {
  const { items: thumbnails, isLoading } = useThumbnailList({
    type: MEDIA_TYPE[mediaType] ?? MEDIA_TYPE.podcast,
  });
  const [search, setSearch] = useState("");
  const [matchingOnly, setMatchingOnly] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isApplying, setIsApplying] = useState(false);

  const requiredLabel = `${ratio.width}:${ratio.height}`;

  const items = useMemo(() => {
    const term = search.trim().toLowerCase();
    return thumbnails
      .filter(
        (item) =>
          !term ||
          item.title?.toLowerCase().includes(term) ||
          item.file_name?.toLowerCase().includes(term)
      )
      .map((item) => ({ ...item, isMatch: matchesRatio(item, ratio) }))
      .filter((item) => !matchingOnly || item.isMatch);
  }, [thumbnails, search, matchingOnly, ratio]);

  const selected = items.find((item) => item._id === selectedId && item.isMatch);

  const close = () => {
    setSearch("");
    setSelectedId(null);
    handleClose();
  };

  const handleUse = async () => {
    if (!selected) return;
    setIsApplying(true);
    try {
      const file = await toFile(selected.image_url, selected.file_name);
      onSelect(file, selected.image_url);
      toast.success(`"${selected.title}" selected for ${label}`);
      close();
    } catch (e) {
      toast.error("Could not use this image. Please try another one.");
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <ModalLayout
      centered
      size="lg"
      show={show}
      handleClose={close}
      title={`Select ${label}`}
      closeIcon={<span className="material-symbols-outlined"> close </span>}
      backdrop={"static"}
    >
      <div className="pro-p-5">
        <p className="pro-font-sm pro-mb-3">
          Choose from the {mediaType} menu thumbnails. Only images with a{" "}
          <strong>{requiredLabel}</strong> ratio can be selected.
        </p>

        <div className={`pro-mb-3 ${Style.toolbar}`}>
          <div className={`input-wrap ${Style.search}`}>
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              className="pro-input lg"
              placeholder="Search by title or file name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className={`pro-font-sm ${Style.toggle}`}>
            <input
              type="checkbox"
              className="form-check-input pro-m-0"
              checked={matchingOnly}
              onChange={(e) => setMatchingOnly(e.target.checked)}
            />
            <span>Show only {requiredLabel}</span>
          </label>
        </div>

        {items.length === 0 ? (
          <div className={Style.empty}>
            <p className="pro-fw-medium pro-mb-1">No thumbnails found</p>
            <p className="pro-font-sm pro-mb-0 pro-opacity-60">
              {isLoading
                ? "Loading..."
                : search || matchingOnly
                ? "Try a different search."
                : `Upload ${mediaType} images in Media > Menu Thumbnails first.`}
            </p>
          </div>
        ) : (
          <div className={Style.list_wrap}>
            <table className={Style.list}>
              <thead>
                <tr>
                  <th></th>
                  <th>Thumbnail</th>
                  <th>Title</th>
                  <th>File Name</th>
                  <th>Dimensions</th>
                  <th>Ratio</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item._id}
                    className={`${item._id === selectedId ? Style.selected : ""} ${
                      item.isMatch ? Style.selectable : Style.mismatch
                    }`}
                    onClick={() => item.isMatch && setSelectedId(item._id)}
                    title={
                      item.isMatch
                        ? item.file_name
                        : `Ratio ${getRatioLabel(item.width, item.height)}, needs ${requiredLabel}`
                    }
                  >
                    <td>
                      <input
                        type="radio"
                        className="form-check-input pro-m-0"
                        name="menu_thumbnail_pick"
                        checked={item._id === selectedId}
                        disabled={!item.isMatch}
                        onChange={() => setSelectedId(item._id)}
                      />
                    </td>
                    <td>
                      <img className={Style.row_thumb} src={item.image_url} alt={item.title} />
                    </td>
                    <td className={Style.name_cell}>{item.title}</td>
                    <td className={`${Style.name_cell} pro-font-xs pro-opacity-60`}>{item.file_name}</td>
                    <td className="pro-nowrap">
                      {item.width} × {item.height}
                    </td>
                    <td>
                      <span className={`${Style.ratio} ${item.isMatch ? Style.ok : Style.bad}`}>
                        {getRatioLabel(item.width, item.height)}
                        {!item.isMatch && ` (needs ${requiredLabel})`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="col-12 pro-mt-4 pro-d-flex pro-justify-end">
          <Button className={"pro-btn-link lg pro-px-5"} onClick={close} type="button">
            Cancel
          </Button>
          <Button
            className={"pro-btn-primary lg pro-mx-3"}
            type="button"
            onClick={handleUse}
            disabled={!selected || isApplying}
          >
            {isApplying ? "Applying..." : "Use Image"}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
};

// button placed under an image field, opens the picker
export const SelectFromThumbnails = ({ mediaType, ratio, label, fieldName, formik, setPreview }) => {
  const [show, setShow] = useState(false);
  const [picked, setPicked] = useState(null);

  const handleSelect = (file, preview) => {
    formik.setFieldValue(fieldName, file);
    formik.setFieldError(fieldName, undefined);
    setPreview?.(preview);
    setPicked({ file, preview });
  };

  // the edit form resets its own previews to the saved image, so show the pick
  // here while the field still holds it (a new upload replaces it)
  const isPickedActive = picked && formik.values?.[fieldName] === picked.file;

  return (
    <>
      <button
        type="button"
        className={`pro-btn-link pro-p-0 pro-font-sm pro-mt-1 ${Style.open_btn}`}
        onClick={() => setShow(true)}
      >
        <span className="material-symbols-outlined">photo_library</span>
        <span>Select from Thumbnails</span>
      </button>
      {isPickedActive && (
        <div className={`pro-mt-2 ${Style.picked}`}>
          <img src={picked.preview} alt={picked.file.name} />
          <span className="pro-font-xs">Selected: {picked.file.name}</span>
        </div>
      )}
      {show && (
        <ThumbnailPickerModal
          show={show}
          handleClose={() => setShow(false)}
          mediaType={mediaType}
          ratio={ratio}
          label={label}
          onSelect={handleSelect}
        />
      )}
    </>
  );
};

export default ThumbnailPickerModal;
